import type { DeviceAsset } from '../asset-interfaces/device-asset.interface';

export type EligibilityActionType =
  | 'enable'
  | 'disable'
  | 'deprovision'
  | 'profile-wipe'
  | 'factory-reset'
  | 'move-ou';

export function isEligibilityAction(value: string): value is EligibilityActionType {
  const actions = new Set<EligibilityActionType>([
    'enable',
    'disable',
    'deprovision',
    'profile-wipe',
    'factory-reset',
    'move-ou',
  ]);
  return actions.has(value as EligibilityActionType);
}

export type SkipReason =
  | 'ALREADY_ENABLED'
  | 'ALREADY_DISABLED'
  | 'ALREADY_DEPROVISIONED'
  | 'STATE_DEPROVISIONED'
  | 'STATE_PRE_PROVISIONED'
  | 'OS_UPDATE_REQUIRED'
  | 'OS_INCOMPATIBLE'
  | 'ALREADY_IN_TARGET_OU'
  // Move to last user's OU only: no last user, or the user has no OU. Never
  // produced by evaluateEligibility — the dispatch filter in saas-device-api
  // records it.
  | 'NO_LAST_USER_OU';

export interface EligibilityResult {
  qualifiedIds: string[];
  skippedByReason: Map<SkipReason, DeviceAsset[]>;
  totals: { selected: number; qualified: number; skipped: number };
}

export interface EligibilityOptions {
  now?: Date;
  targetOrgUnitPath?: string;
}

export const SKIP_REASON_LABELS: Record<SkipReason, string> = {
  ALREADY_ENABLED: 'Device already enabled',
  ALREADY_DISABLED: 'Device already disabled',
  ALREADY_DEPROVISIONED: 'Device already deprovisioned',
  STATE_DEPROVISIONED: 'Deprovisioned state',
  STATE_PRE_PROVISIONED: 'Pre-provisioned state',
  OS_UPDATE_REQUIRED: 'Needs OS update',
  OS_INCOMPATIBLE: 'Past AUE',
  ALREADY_IN_TARGET_OU: 'Device already in selected OU',
  NO_LAST_USER_OU: 'No last user OU found',
};

export const SKIP_REASON_ZENDESK_URL =
  'https://amplifiedlabs.zendesk.com/hc/en-us/articles/51106328979091-Understanding-Device-Action-Eligibility-and-State-Rules';

const M136_ACTIONS = new Set<EligibilityActionType>(['profile-wipe', 'factory-reset']);

const M136_MIN_MAJOR_VERSION = 136;

export function requiresM136(action: EligibilityActionType): boolean {
  return M136_ACTIONS.has(action);
}

export function parseChromeMajorVersion(osVersion: string | undefined): number | null {
  if (!osVersion) return null;
  const firstSegment = osVersion.split('.')[0];
  const major = Number.parseInt(firstSegment, 10);
  return Number.isFinite(major) ? major : null;
}

export function isAueExpired(
  aue: Date | string | number | undefined,
  now: Date,
): boolean {
  if (aue == null) return false;
  let parsed: Date;
  if (aue instanceof Date) {
    parsed = aue;
  } else if (typeof aue === 'number') {
    parsed = new Date(aue);
  } else {
    // Google Admin API returns autoUpdateExpiration as epoch ms in a string
    // (int64 format). new Date("1735689600000") returns Invalid Date, so
    // detect numeric strings and parse as a number first.
    const asNum = Number(aue);
    parsed = Number.isFinite(asNum) ? new Date(asNum) : new Date(aue);
  }
  const time = parsed.getTime();
  if (!Number.isFinite(time)) return false;
  return time < now.getTime();
}

// PRE_PROVISIONED_DISABLED is a Google status returned for devices that
// were pre-provisioned and subsequently disabled — neither yet enrolled
// nor active. PR #651 added it to the blocked-states set after QA hit it;
// for eligibility purposes it behaves identically to PRE_PROVISIONED for
// every action (the device is not actionable until it leaves the
// pre-provisioned state).
const PRE_PROVISIONED_STATES = new Set(['PRE_PROVISIONED', 'PRE_PROVISIONED_DISABLED']);

export function isStatusBlocked(
  action: EligibilityActionType,
  status: string | undefined,
): SkipReason | null {
  const s = (status ?? '').toUpperCase();
  const isPreProvisioned = PRE_PROVISIONED_STATES.has(s);

  switch (action) {
    case 'enable':
      if (s === 'ACTIVE') return 'ALREADY_ENABLED';
      if (s === 'DEPROVISIONED') return 'STATE_DEPROVISIONED';
      if (isPreProvisioned) return 'STATE_PRE_PROVISIONED';
      return null;
    case 'disable':
      if (s === 'DISABLED') return 'ALREADY_DISABLED';
      if (s === 'DEPROVISIONED') return 'STATE_DEPROVISIONED';
      if (isPreProvisioned) return 'STATE_PRE_PROVISIONED';
      return null;
    case 'deprovision':
      if (s === 'DEPROVISIONED') return 'ALREADY_DEPROVISIONED';
      if (isPreProvisioned) return 'STATE_PRE_PROVISIONED';
      return null;
    case 'profile-wipe':
    case 'factory-reset':
      if (s === 'DEPROVISIONED') return 'STATE_DEPROVISIONED';
      if (isPreProvisioned) return 'STATE_PRE_PROVISIONED';
      return null;
    case 'move-ou':
      return null;
    default: {
      const _exhaustive: never = action;
      return _exhaustive;
    }
  }
}

export function evaluateEligibility(
  action: EligibilityActionType,
  devices: DeviceAsset[],
  opts: EligibilityOptions = {},
): EligibilityResult {
  const now = opts.now ?? new Date();
  const qualifiedIds: string[] = [];
  const skippedByReason = new Map<SkipReason, DeviceAsset[]>();

  const skip = (reason: SkipReason, device: DeviceAsset) => {
    const bucket = skippedByReason.get(reason);
    if (bucket) {
      bucket.push(device);
    } else {
      skippedByReason.set(reason, [device]);
    }
  };

  for (const device of devices) {
    const google = device.vendorProperties?.google;

    if (action === 'move-ou') {
      if (
        opts.targetOrgUnitPath != null &&
        google?.orgUnitPath === opts.targetOrgUnitPath
      ) {
        skip('ALREADY_IN_TARGET_OU', device);
      } else {
        qualifiedIds.push(String(device.id));
      }
      continue;
    }

    const statusBlock = isStatusBlocked(action, google?.status);
    if (statusBlock) {
      skip(statusBlock, device);
      continue;
    }

    if (requiresM136(action)) {
      const major = parseChromeMajorVersion(google?.osVersion);
      if (major == null || major < M136_MIN_MAJOR_VERSION) {
        if (isAueExpired(google?.autoUpdateExpiration, now)) {
          skip('OS_INCOMPATIBLE', device);
        } else {
          skip('OS_UPDATE_REQUIRED', device);
        }
        continue;
      }
    }

    qualifiedIds.push(String(device.id));
  }

  return {
    qualifiedIds,
    skippedByReason,
    totals: {
      selected: devices.length,
      qualified: qualifiedIds.length,
      skipped: devices.length - qualifiedIds.length,
    },
  };
}
